import { FunctionComponent, useState } from 'react';
import { Dialog, TableCell } from '../../tailwindcomponents';
import { TaskNote } from '../../types/tasks';

type Props = {
    notes: TaskNote[];
    loaded: boolean;
};

const RemarksCell: FunctionComponent<Props> = ({ notes, loaded }) => {
    const [expanded, setExpanded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    if (!loaded) {
        return (
            <TableCell>
                <span className="text-gray-400 italic text-xs">Loading...</span>
            </TableCell>
        );
    }

    if (notes.length === 0) {
        return (
            <TableCell>
                <span className="text-gray-400 italic text-xs">No notes</span>
            </TableCell>
        );
    }

    // Filter to get only current versions, ordered by created_date descending
    const currentVersions = notes
        .filter(n => n.current_version && !n.is_deleted)
        .sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime());

    if (currentVersions.length === 0) {
        return (
            <TableCell>
                <span className="text-gray-400 italic text-xs">No notes</span>
            </TableCell>
        );
    }

    if (currentVersions.length === 1 && currentVersions[0].text.length <= 80) {
        const note = currentVersions[0];
        return (
            <TableCell className="max-w-[300px]">
                <div className="flex items-start gap-1">
                    <span className="text-xs">{note.text}</span>
                    {note.version_id > 1 && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded whitespace-nowrap">
                            Edited
                        </span>
                    )}
                </div>
            </TableCell>
        );
    }

    const latestNote = currentVersions[0];
    const remainingCount = currentVersions.length - 1;

    return (
        <TableCell className="max-w-[300px]">
            {!expanded ? (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="w-full text-left bg-transparent border-0 p-0 cursor-pointer group"
                >
                    <div className="flex items-start gap-1">
                        <p className="text-xs line-clamp-1 m-0 group-hover:text-blue-700 flex-1">
                            {latestNote.text}
                        </p>
                        {latestNote.version_id > 1 && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded whitespace-nowrap">
                                Edited
                            </span>
                        )}
                    </div>
                    {remainingCount > 0 && (
                        <span className="text-xs text-blue-600 font-medium mt-0.5 inline-block">
                            +{remainingCount} more
                        </span>
                    )}
                </button>
            ) : (
                <div className="max-h-[120px] overflow-y-auto">
                    {currentVersions.slice(0, 3).map((n, i) => (
                        <div key={i} className="mb-1 last:mb-0">
                            <div className="flex items-start justify-between gap-1">
                                <div>
                                    <span className="text-xs text-gray-500 block">{n.created_date_text}</span>
                                    <span className="text-xs text-gray-600 font-medium">{n.created_by_employee_name}</span>
                                </div>
                                {n.version_id > 1 && (
                                    <span className="text-[9px] bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded whitespace-nowrap">
                                        v{n.version_id}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs m-0 line-clamp-2">{n.text}</p>
                        </div>
                    ))}
                    {currentVersions.length > 3 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setDialogOpen(true); }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 p-0 border-0 bg-transparent cursor-pointer"
                        >
                            View full log ({currentVersions.length} entries)
                        </button>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                        className="text-xs text-gray-500 hover:text-gray-700 ml-2 p-0 border-0 bg-transparent cursor-pointer"
                    >
                        Collapse
                    </button>
                </div>
            )}

            {dialogOpen && (
                <Dialog
                    fullScreen={false}
                    show_options={false}
                    strict={false}
                    max_width={'md'}
                    onClose={() => setDialogOpen(false)}
                    title="Task Notes Log"
                    visible={dialogOpen}
                    context={
                        <div className="p-4">
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                                {currentVersions.map((n, i) => (
                                    <div key={i} className="border-b border-line pb-2 last:border-b-0">
                                        <div className="flex items-start justify-between mb-1">
                                            <div>
                                                <span className="text-xs font-semibold text-gray-700 block">
                                                    {n.created_by_employee_name}
                                                </span>
                                                <span className="text-xs text-gray-500 block">{n.created_date_text}</span>
                                            </div>
                                            {n.version_id > 1 && (
                                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                                    v{n.version_id}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap break-words">
                                            {n.text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                />
            )}
        </TableCell>
    );
};

export default RemarksCell;
